/**
 * Um campo do wizard: rótulo, controle, de onde veio o valor e, quando a
 * plataforma tem proposta, "voltar à proposta" (spec 2026-10-09 §5).
 *
 * A origem é texto, não cor: "do tenant", "padrão: <motivo>", "da conversa",
 * "você". O erro do campo fica junto dele (a11y: `error` do Mantine liga o
 * `aria-describedby`). Barlow no rótulo, JetBrains Mono no valor fixo.
 */
import { Anchor, Group, NumberInput, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core'
import { IconArrowBackUp } from '@tabler/icons-react'
import type { Origem } from '@/api/rascunhoDeAmbiente'
import { Dado } from '../components/Dado'
import type { UseRascunho } from './useRascunho'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

export function textoDaOrigem(origem: Origem | undefined, naFila: boolean): string | null {
  if (naFila) return 'você · salvando'
  if (!origem) return null
  switch (origem.origem) {
    case 'tenant': return 'do tenant'
    case 'padrao': return `padrão: ${origem.motivo ?? 'proposta da plataforma'}`
    case 'voce': return 'você'
    case 'conversa': return 'da conversa'
    case 'diagrama': return 'do diagrama'
    case 'chamado': return 'do chamado'
    default: return null
  }
}

export function OrigemDoCampo({ r, caminho }: { r: UseRascunho; caminho: string }) {
  const atual = r.valor(caminho)
  const proposta = r.rascunho?.propostas[caminho]
  const texto = textoDaOrigem(r.rascunho?.origens[caminho], r.naFila(caminho))
  const podeVoltar = proposta !== undefined && String(atual ?? '') !== proposta
  if (!texto && !podeVoltar) return null
  return (
    <Group gap={8} wrap="wrap" align="center">
      {texto && <Text size="xs" c="dimmed" style={BARLOW}>{texto}</Text>}
      {podeVoltar && (
        <Anchor component="button" type="button" size="xs" onClick={() => r.voltarAProposta(caminho)}>
          <Group gap={4} wrap="nowrap">
            <IconArrowBackUp size={14} stroke={1.5} aria-hidden />
            <span>voltar à proposta ({proposta})</span>
          </Group>
        </Anchor>
      )}
    </Group>
  )
}

function erroDe(r: UseRascunho, caminho: string): string | undefined {
  return r.rascunho?.invalidos.find((i) => i.campo === caminho)?.o_que_dizer
}

function faltaDe(r: UseRascunho, caminho: string): boolean {
  return Boolean(r.rascunho?.faltando.includes(caminho))
}

interface CampoProps {
  r: UseRascunho
  caminho: string
  rotulo: string
  tipo?: 'texto' | 'numero' | 'escolha' | 'sim_nao' | 'fixo'
  opcoes?: string[]
  descricao?: string
  /** Os limites que o contrato aplica (`limites` do rascunho): mostrados e impostos no campo. */
  limites?: { minimo: number | null; maximo: number | null }
  /** Na escolha: o que mais muda junto (ex.: apagar a quantidade de HSMs ao escolher "nenhum"). */
  aoEscolher?: (valor: string) => void
}

export function textoDosLimites(limites: CampoProps['limites']): string | null {
  if (!limites) return null
  const { minimo, maximo } = limites
  if (minimo !== null && maximo !== null) return `De ${minimo} a ${maximo}.`
  if (maximo !== null) return `Até ${maximo}.`
  if (minimo !== null) return `A partir de ${minimo}.`
  return null
}

export function Campo({ r, caminho, rotulo, tipo = 'texto', opcoes = [], descricao, limites, aoEscolher }: CampoProps) {
  const valor = r.valor(caminho)
  const erro = erroDe(r, caminho) ?? (faltaDe(r, caminho) ? 'falta preencher' : undefined)
  const label = <Text size="sm" fw={500} style={BARLOW} span>{rotulo}</Text>
  let controle: React.ReactNode
  if (tipo === 'fixo') {
    controle = (
      <Stack gap={2}>
        {label}
        <Dado>{valor === undefined || valor === null ? '—' : String(valor)}</Dado>
      </Stack>
    )
  } else if (tipo === 'numero') {
    controle = (
      <NumberInput label={label} error={erro} allowDecimal={false}
                   description={[descricao, textoDosLimites(limites)].filter(Boolean).join(' ') || undefined}
                   min={limites?.minimo ?? undefined} max={limites?.maximo ?? undefined}
                   value={valor === undefined || valor === null || valor === '' ? '' : Number(valor)}
                   onChange={(v) => r.editar(caminho, v === '' ? '' : Number(v))}
                   styles={{ input: { fontFamily: '"JetBrains Mono", monospace' } }} />
    )
  } else if (tipo === 'escolha') {
    controle = (
      <Select label={label} description={descricao} error={erro} data={opcoes}
              value={valor === undefined || valor === null ? null : String(valor)}
              onChange={(v) => {
                if (v === null) return
                r.editar(caminho, v)
                aoEscolher?.(v)
              }} allowDeselect={false} />
    )
  } else if (tipo === 'sim_nao') {
    // Pergunta explícita: sem valor gravado, nenhuma opção aparece marcada.
    controle = (
      <Stack gap={4}>
        {label}
        {descricao && <Text size="xs" c="dimmed" style={BARLOW}>{descricao}</Text>}
        <SegmentedControl aria-label={rotulo}
                          data={[{ label: 'Sim', value: 'sim' }, { label: 'Não', value: 'nao' }]}
                          value={valor === 'sim' || valor === 'nao' ? String(valor) : ''}
                          onChange={(v) => r.editar(caminho, v)} />
        {erro && <Text size="xs" c="red" role="alert" style={BARLOW}>{erro}</Text>}
      </Stack>
    )
  } else {
    controle = (
      <TextInput label={label} description={descricao} error={erro}
                 value={valor === undefined || valor === null ? '' : String(valor)}
                 onChange={(e) => r.editar(caminho, e.currentTarget.value)}
                 styles={{ input: { fontFamily: '"JetBrains Mono", monospace' } }} />
    )
  }
  return (
    <Stack gap={4}>
      {controle}
      <OrigemDoCampo r={r} caminho={caminho} />
    </Stack>
  )
}
